/* ============================================================================
 * STP Engine Mega Menu — "Parts by Engine"
 * ----------------------------------------------------------------------------
 * Interaction model (mirrors the ATP/i40 builds):
 *   - Click-only by default: clicking a manufacturer opens its panel.
 *   - Manufacturer JSON (slim placements: model > component > {handle, sku})
 *     is fetched only on first open, then cached.
 *   - Product tiles are hydrated live from /products/<handle>.js only when
 *     their component is opened — title, image, price, compare-at,
 *     availability and URL always match Shopify Admin.
 *   - sessionStorage cache (15 min TTL) + in-memory cache, max 5 concurrent
 *     product requests, duplicate requests coalesced, requests for tiles no
 *     longer in the DOM are skipped, 404s remove the tile and log a warning.
 *   - Escape and outside click close. Arrow keys move across manufacturers.
 *   - Mobile: a "Parts by Engine" entry is appended beneath the original
 *     links in the theme drawer; it opens a full-screen drill-down sheet
 *     (manufacturers > models > components > products) with a back button.
 * Everything is namespaced stp-em-* so native theme nav scripts ignore it.
 * ==========================================================================*/
(function () {
  'use strict';
  if (window.__stpEngineMenuInit) return;
  window.__stpEngineMenuInit = true;

  var root = document.getElementById('stp-engine-menu');
  if (!root) return;

  /* ---------------- config from the section ---------------- */
  var STAFF = /stp-em-staff/.test(window.location ? window.location.search : '');
  var cfg = {
    clickOnly: root.getAttribute('data-click-only') !== 'false',
    columns: parseInt(root.getAttribute('data-columns'), 10) || 4,
    maxInitial: parseInt(root.getAttribute('data-max-initial'), 10) || 8,
    showImages: root.getAttribute('data-show-images') !== 'false',
    showPrices: root.getAttribute('data-show-prices') !== 'false',
    showSoldOut: root.getAttribute('data-show-soldout') === 'true',
    showMissing: root.getAttribute('data-show-missing') === 'true',
    hideEmpty: root.getAttribute('data-hide-empty') !== 'false',
    maxHeight: parseInt(root.getAttribute('data-max-height'), 10) || 560,
    placeholder: root.getAttribute('data-placeholder') || '',
    mobileHeading: root.getAttribute('data-mobile-heading') || 'Parts by Engine',
    currency: root.getAttribute('data-currency') || 'USD'
  };
  if (STAFF) { cfg.showSoldOut = true; cfg.showMissing = true; cfg.hideEmpty = false; }
  if (window.console) console.info('[stp-engine-menu] config', {
    showSoldOut: cfg.showSoldOut, showMissing: cfg.showMissing, hideEmpty: cfg.hideEmpty, staffOverride: STAFF
  });

  var AUDIT = /stp-em-audit/.test(window.location ? window.location.search : '');
  window.__stpEmAudit = window.__stpEmAudit || [];
  function audit(handle, reason) {
    window.__stpEmAudit.push({ handle: handle, reason: reason });
    if (AUDIT && window.console) console.warn('[stp-engine-menu audit]', reason, handle);
  }

  var panel = root.querySelector('.stp-em-panel');
  var triggers = Array.prototype.slice.call(root.querySelectorAll('.stp-em-trigger'));
  if (!panel || !triggers.length) return;
  panel.style.setProperty('--stp-em-max-h', cfg.maxHeight + 'px');
  panel.style.setProperty('--stp-em-cols', cfg.columns);

  /* ---------------- tiny utils ---------------- */
  var menuCache = {};        /* slug -> menu json (or promise) */
  var productMem = {};       /* handle -> product json | 'MISSING' | promise */
  var TTL = 15 * 60 * 1000;

  function money(cents) {
    if (cents == null || isNaN(cents)) return '';
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: cfg.currency }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function ssGet(key) {
    try {
      var raw = window.sessionStorage.getItem(key);
      if (!raw) return null;
      var obj = JSON.parse(raw);
      if (!obj || (Date.now() - obj.t) > TTL) return null;
      return obj.v;
    } catch (e) { return null; }
  }
  function ssSet(key, v) {
    try { window.sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), v: v })); } catch (e) {}
  }

  /* ---------------- product fetch queue (max 5 concurrent) ---------------- */
  var queue = [], active = 0, MAX_ACTIVE = 5;
  function pump() {
    while (active < MAX_ACTIVE && queue.length) {
      var job = queue.shift();
      if (job.el && !job.el.isConnected) { if (job.done) job.done(null); continue; }
      active++;
      (function (job) {
        fetchProduct(job.handle).then(function (p) {
          active--; if (job.done) job.done(p); pump();
        });
      })(job);
    }
  }
  function fetchProduct(handle) {
    if (productMem[handle] && typeof productMem[handle].then === 'function') return productMem[handle];
    if (productMem[handle]) return Promise.resolve(productMem[handle] === 'MISSING' ? null : productMem[handle]);
    var cached = ssGet('stpEM:p:' + handle);
    if (cached) { productMem[handle] = cached; return Promise.resolve(cached === 'MISSING' ? null : cached); }
    var pr = fetch('/products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
      .then(function (r) {
        if (r.status === 404) return 'MISSING';
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (p) {
        var slim = p === 'MISSING' ? 'MISSING' : {
          title: p.title, url: p.url, available: p.available,
          price: p.price, compare_at_price: p.compare_at_price,
          image: (p.featured_image || (p.images && p.images[0]) || '')
        };
        productMem[handle] = slim;
        ssSet('stpEM:p:' + handle, slim);
        return slim === 'MISSING' ? null : slim;
      })
      .catch(function () { productMem[handle] = undefined; return null; });
    productMem[handle] = pr;
    return pr;
  }

  /* ---------------- open / close ---------------- */
  var openSlug = null, hoverTimer = null;

  function closePanel() {
    if (!openSlug) return;
    openSlug = null;
    panel.hidden = true;
    root.classList.remove('stp-em-open');
    triggers.forEach(function (t) { t.setAttribute('aria-expanded', 'false'); t.classList.remove('is-active'); });
  }
  function openManufacturer(btn) {
    var slug = btn.getAttribute('data-slug');
    if (openSlug === slug) { closePanel(); return; }
    openSlug = slug;
    triggers.forEach(function (t) {
      var on = t === btn;
      t.setAttribute('aria-expanded', on ? 'true' : 'false');
      t.classList.toggle('is-active', on);
    });
    root.classList.add('stp-em-open');
    panel.hidden = false;
    panel.innerHTML = '<div class="stp-em-loading" role="status">Loading ' + esc(btn.textContent.trim()) + '…</div>';
    var tplId = btn.getAttribute('data-tpl');
    if (tplId) { renderTemplatePanel(tplId); return; }
    loadMenu(slug, btn.getAttribute('data-json')).then(function (menu) {
      if (openSlug !== slug) return;
      if (!menu) { panel.innerHTML = '<div class="stp-em-loading">Could not load this menu. Please refresh and try again.</div>'; return; }
      renderPanel(menu, slug);
    });
  }
  function renderTemplatePanel(tplId) {
    var tpl = document.getElementById(tplId);
    if (!tpl) { panel.innerHTML = '<div class="stp-em-loading">Category menu is not installed.</div>'; return; }
    panel.innerHTML = '';
    panel.appendChild(tpl.content.cloneNode(true));
    panel.querySelector('.stp-em-models').addEventListener('click', function (e) {
      var b = e.target.closest('.stp-em-model');
      if (!b) return;
      var i = b.getAttribute('data-i');
      panel.querySelectorAll('.stp-em-model').forEach(function (x) { x.classList.toggle('is-active', x === b); });
      panel.querySelectorAll('.stp-em-typeset').forEach(function (x) { x.hidden = x.getAttribute('data-i') !== i; });
    });
  }
  function loadMenu(slug, url) {
    if (menuCache[slug] && typeof menuCache[slug].then === 'function') return menuCache[slug];
    if (menuCache[slug]) return Promise.resolve(menuCache[slug]);
    var cached = ssGet('stpEM:m:' + slug);
    if (cached) { menuCache[slug] = cached; return Promise.resolve(cached); }
    function attempt() {
      return fetch(url, { credentials: 'same-origin', cache: 'force-cache' })
        .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); });
    }
    var pr = attempt()
      .catch(function () {
        return new Promise(function (res) { window.setTimeout(res, 900); }).then(attempt);
      })
      .then(function (m) { menuCache[slug] = m; ssSet('stpEM:m:' + slug, m); return m; })
      .catch(function () { menuCache[slug] = undefined; return null; });
    menuCache[slug] = pr;
    return pr;
  }

  /* ---------------- desktop panel rendering ---------------- */
  function visibleComponents(model) {
    return (model.components || []).filter(function (c) {
      if (c.url || c.collection) return true;
      return cfg.hideEmpty ? (c.products && c.products.length) : true;
    });
  }
  function renderPanel(menu, slug) {
    var models = (menu.models || []).filter(function (mo) {
      return cfg.hideEmpty ? visibleComponents(mo).length : true;
    });
    if (!models.length) {
      panel.innerHTML = '<div class="stp-em-loading">No products in this menu yet — check back soon.</div>';
      return;
    }
    var html = '<div class="stp-em-grid">';
    html += '<ul class="stp-em-models" role="tablist" aria-label="' + esc(menu.manufacturer) + ' engine models">';
    models.forEach(function (mo, i) {
      html += '<li><button type="button" role="tab" class="stp-em-model' + (i === 0 ? ' is-active' : '') + '" data-i="' + i + '" aria-selected="' + (i === 0) + '">' + esc(mo.model) + '</button></li>';
    });
    html += '</ul><ul class="stp-em-comps" aria-label="Part categories"></ul><div class="stp-em-products"></div></div>';
    panel.innerHTML = html;
    var state = { menu: menu, models: models, modelIdx: 0, compIdx: 0 };
    var firstComps = visibleComponents(models[0]);
    for (var fi = 0; fi < firstComps.length; fi++) { if (!firstComps[fi].collection && !firstComps[fi].url) { state.compIdx = fi; break; } }
    panel.__stpState = state;
    panel.querySelector('.stp-em-models').addEventListener('click', function (e) {
      var b = e.target.closest('.stp-em-model');
      if (!b) return;
      state.modelIdx = parseInt(b.getAttribute('data-i'), 10) || 0;
      state.compIdx = 0;
      panel.querySelectorAll('.stp-em-model').forEach(function (x) {
        var on = x === b;
        x.classList.toggle('is-active', on);
        x.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      renderComponents(state);
    });
    renderComponents(state);
  }
  function renderComponents(state) {
    var list = panel.querySelector('.stp-em-comps');
    var comps = visibleComponents(state.models[state.modelIdx]);
    var html = '';
    comps.forEach(function (c, i) {
      if (c.url || c.collection) {
        html += '<li><a class="stp-em-comp stp-em-complink" href="' + esc(c.url || ('/collections/' + c.collection)) + '">' +
          esc(c.component) + '<span class="stp-em-count">' + (c.count || c.products.length) + '</span><span class="stp-em-go" aria-hidden="true">&#8599;</span></a></li>';
      } else {
        html += '<li><button type="button" class="stp-em-comp' + (i === state.compIdx ? ' is-active' : '') + '" data-i="' + i + '">' +
          esc(c.component) + '<span class="stp-em-count">' + c.products.length + '</span></button></li>';
      }
    });
    list.innerHTML = html || '<li class="stp-em-empty">No categories yet.</li>';
    list.onclick = function (e) {
      var b = e.target.closest('.stp-em-comp');
      if (!b || b.tagName === 'A') return;
      state.compIdx = parseInt(b.getAttribute('data-i'), 10) || 0;
      list.querySelectorAll('.stp-em-comp').forEach(function (x) { x.classList.toggle('is-active', x === b); });
      renderProducts(state);
    };
    renderProducts(state);
  }
  function renderProducts(state) {
    var wrap = panel.querySelector('.stp-em-products');
    var comps = visibleComponents(state.models[state.modelIdx]);
    var comp = comps[state.compIdx];
    if (!comp) { wrap.innerHTML = '<div class="stp-em-empty">Pick a category on the left.</div>'; return; }
    if (comp.url || comp.collection) { wrap.innerHTML = '<a class="stp-em-browse" href="' + esc(comp.url || ('/collections/' + comp.collection)) + '">Browse all ' + esc(comp.component.toLowerCase()) + ' &#8599;</a>'; return; }
    if (!comp.products.length) { wrap.innerHTML = '<div class="stp-em-empty">Nothing listed here yet — call us, we may have it on the shelf.</div>'; return; }
    var initial = comp.products.slice(0, cfg.maxInitial);
    var rest = comp.products.slice(cfg.maxInitial);
    wrap.innerHTML = '<div class="stp-em-cards" style="--stp-em-cols:' + cfg.columns + '">' +
      initial.map(cardSkeleton).join('') + '</div>' +
      (rest.length ? '<button type="button" class="stp-em-more">Show ' + rest.length + ' more</button>' : '');
    hydrateCards(wrap);
    var more = wrap.querySelector('.stp-em-more');
    if (more) {
      more.addEventListener('click', function () {
        var cards = wrap.querySelector('.stp-em-cards');
        cards.insertAdjacentHTML('beforeend', rest.map(cardSkeleton).join(''));
        more.parentNode.removeChild(more);
        hydrateCards(wrap);
      });
    }
  }
  function cardSkeleton(p) {
    return '<a class="stp-em-card is-loading" data-handle="' + esc(p.handle) + '" data-sku="' + esc(p.sku || '') + '" href="/products/' + esc(p.handle) + '" tabindex="-1">' +
      (cfg.showImages ? '<span class="stp-em-img"></span>' : '') +
      '<span class="stp-em-title">&nbsp;</span>' +
      (cfg.showPrices ? '<span class="stp-em-price">&nbsp;</span>' : '') +
      '</a>';
  }
  function hydrateCards(scope) {
    Array.prototype.forEach.call(scope.querySelectorAll('.stp-em-card.is-loading'), function (el) {
      el.classList.remove('is-loading');
      el.classList.add('is-pending');
      queue.push({ handle: el.getAttribute('data-handle'), el: el, done: function (p) { fillCard(el, p); } });
    });
    pump();
  }
  function removeCard(el, reason) {
    audit(el.getAttribute('data-handle'), reason);
    var cards = el.closest ? el.closest('.stp-em-cards') : null;
    el.parentNode && el.parentNode.removeChild(el);
    syncAfterRemoval(cards);
  }
  function syncAfterRemoval(cards) {
    if (!cards || !cards.isConnected) return;
    var left = cards.querySelectorAll('.stp-em-card').length;
    /* keep the active category count badge honest */
    var badge = panel.querySelector('.stp-em-comp.is-active .stp-em-count');
    if (badge) badge.textContent = left;
    if (!left) {
      cards.insertAdjacentHTML('afterend', '<div class="stp-em-empty">These just sold or came off the shelf — call us at the number up top, we may have more in the warehouse.</div>');
      var more = cards.parentNode && cards.parentNode.querySelector('.stp-em-more');
      if (more) more.parentNode.removeChild(more);
    }
  }
  function fillCard(el, p) {
    if (!el.isConnected) return;
    if (!p) {
      if (productMem[el.getAttribute('data-handle')] === 'MISSING') {
        if (window.console) console.warn('[stp-engine-menu] missing/unpublished product:', el.getAttribute('data-handle'));
        if (cfg.showMissing) {
          audit(el.getAttribute('data-handle'), 'missing-or-unpublished (shown as placeholder)');
          el.classList.remove('is-pending');
          el.classList.add('is-missing');
          el.removeAttribute('href');
          el.innerHTML = (cfg.showImages ? '<span class="stp-em-img stp-em-img--none" aria-hidden="true"></span>' : '') +
            '<span class="stp-em-title">' + esc(el.getAttribute('data-sku') || el.getAttribute('data-handle')) + '</span>' +
            '<span class="stp-em-missing-chip">Not published</span>';
        } else {
          removeCard(el, 'missing-or-unpublished (404)');
        }
      } else {
        el.classList.remove('is-pending');
        renderCardContent(el, null);
      }
      return;
    }
    if (!p.available && !cfg.showSoldOut) { removeCard(el, 'sold out (hidden by setting)'); return; }
    el.classList.remove('is-pending');
    renderCardContent(el, p);
  }
  function renderCardContent(el, p) {
    var img = (p && p.image) || cfg.placeholder;
    var html = '';
    if (cfg.showImages) {
      html += img
        ? '<span class="stp-em-img"><img loading="lazy" src="' + esc(img) + '" alt=""></span>'
        : '<span class="stp-em-img stp-em-img--none" aria-hidden="true"></span>';
    }
    html += '<span class="stp-em-title">' + esc(p ? p.title : el.getAttribute('data-handle')) + '</span>';
    if (cfg.showPrices && p) {
      html += '<span class="stp-em-price">' + money(p.price) +
        (p.compare_at_price && p.compare_at_price > p.price ? ' <s>' + money(p.compare_at_price) + '</s>' : '') + '</span>';
    }
    if (p && !p.available) { html += '<span class="stp-em-sold">Sold out</span>'; el.classList.add('is-soldout'); }
    el.innerHTML = html;
    el.removeAttribute('tabindex');
    if (p && p.url) el.setAttribute('href', p.url);
  }

  /* ---------------- desktop events ---------------- */
  triggers.forEach(function (btn, i) {
    btn.addEventListener('click', function (e) { e.preventDefault(); openManufacturer(btn); });
    if (!cfg.clickOnly) {
      btn.addEventListener('mouseenter', function () {
        window.clearTimeout(hoverTimer);
        hoverTimer = window.setTimeout(function () { if (openSlug !== btn.getAttribute('data-slug')) openManufacturer(btn); }, 150);
      });
      btn.addEventListener('mouseleave', function () { window.clearTimeout(hoverTimer); });
    }
    btn.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        e.preventDefault();
        var next = e.key === 'ArrowRight' ? (i + 1) % triggers.length : (i - 1 + triggers.length) % triggers.length;
        triggers[next].focus();
      }
    });
  });
  document.addEventListener('click', function (e) {
    if (openSlug && !root.contains(e.target)) closePanel();
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && openSlug) { closePanel(); }
  });

  /* ---------------- mobile: drawer entry + full-screen sheet ---------------- */
  function buildMobile() {
    var drawerNav = document.querySelector('.main-menu__content .main-nav');
    if (!drawerNav || document.getElementById('stp-em-mob-entry')) return;
    var li = document.createElement('li');
    li.id = 'stp-em-mob-entry';
    li.className = 'stp-em-mob-li';
    li.innerHTML = '<button type="button" class="stp-em-mob-open">' + esc(cfg.mobileHeading) + ' <span aria-hidden="true">&rsaquo;</span></button>';
    drawerNav.appendChild(li);

    var sheet = document.createElement('div');
    sheet.id = 'stp-em-sheet';
    sheet.hidden = true;
    sheet.setAttribute('role', 'dialog');
    sheet.setAttribute('aria-label', cfg.mobileHeading);
    sheet.innerHTML = '<div class="stp-em-sheet-bar"><button type="button" class="stp-em-back">&lsaquo; Back</button><span class="stp-em-sheet-title"></span><button type="button" class="stp-em-close" aria-label="Close">&times;</button></div><div class="stp-em-sheet-body"></div>';
    document.body.appendChild(sheet);

    var body = sheet.querySelector('.stp-em-sheet-body');
    var title = sheet.querySelector('.stp-em-sheet-title');
    var stack = [];

    function show(view) { stack.push(view); paint(); }
    function back() { stack.pop(); if (!stack.length) { close(); return; } paint(); }
    function close() { sheet.hidden = true; document.documentElement.classList.remove('stp-em-lock'); stack = []; }
    function paint() {
      var v = stack[stack.length - 1];
      title.textContent = v.title;
      body.innerHTML = v.html;
      body.scrollTop = 0;
      if (v.after) v.after();
    }
    function listHtml(items) {
      return '<ul class="stp-em-mlist">' + items.map(function (x, i) {
        return '<li><button type="button" data-i="' + i + '">' + esc(x.label) + (x.count != null ? '<span class="stp-em-count">' + x.count + '</span>' : '') + ' <span aria-hidden="true">&rsaquo;</span></button></li>';
      }).join('') + '</ul>';
    }

    li.querySelector('.stp-em-mob-open').addEventListener('click', function () {
      sheet.hidden = false;
      document.documentElement.classList.add('stp-em-lock');
      var items = triggers.map(function (t) { return { label: t.textContent.trim(), slug: t.getAttribute('data-slug'), json: t.getAttribute('data-json'), tpl: t.getAttribute('data-tpl') }; });
      show({
        title: cfg.mobileHeading,
        html: listHtml(items),
        after: function () {
          body.querySelector('.stp-em-mlist').onclick = function (e) {
            var b = e.target.closest('button'); if (!b) return;
            var it = items[parseInt(b.getAttribute('data-i'), 10)];
            body.innerHTML = '<div class="stp-em-loading">Loading…</div>';
            if (it.tpl) { showSystems(it.tpl, it.label); return; }
            loadMenu(it.slug, it.json).then(function (menu) { if (menu) showModels(menu); });
          };
        }
      });
    });
    function showSystems(tplId, heading) {
      var tpl = document.getElementById(tplId);
      if (!tpl) return;
      var names = Array.prototype.map.call(tpl.content.querySelectorAll('.stp-em-model'), function (b) { return b.textContent.trim(); });
      show({
        title: heading || 'Shop by Category',
        html: listHtml(names.map(function (n) { return { label: n }; })),
        after: function () {
          body.querySelector('.stp-em-mlist').onclick = function (e) {
            var b = e.target.closest('button'); if (!b) return;
            var i = b.getAttribute('data-i');
            var set = tpl.content.querySelector('.stp-em-typeset[data-i="' + i + '"]');
            if (!set) return;
            var links = Array.prototype.map.call(set.querySelectorAll('.stp-em-type'), function (a) {
              return '<li><a href="' + esc(a.getAttribute('href')) + '">' + a.innerHTML + ' <span aria-hidden="true">&rsaquo;</span></a></li>';
            }).join('');
            show({ title: names[parseInt(i, 10)] || 'Categories', html: '<ul class="stp-em-mlist">' + links + '</ul>' });
          };
        }
      });
    }
    function showModels(menu) {
      var models = (menu.models || []).filter(function (mo) { return cfg.hideEmpty ? visibleComponents(mo).length : true; });
      show({
        title: menu.manufacturer,
        html: listHtml(models.map(function (mo) { return { label: mo.model }; })),
        after: function () {
          body.querySelector('.stp-em-mlist').onclick = function (e) {
            var b = e.target.closest('button'); if (!b) return;
            showComps(menu, models[parseInt(b.getAttribute('data-i'), 10)]);
          };
        }
      });
    }
    function showComps(menu, model) {
      var comps = visibleComponents(model);
      show({
        title: model.model,
        html: listHtml(comps.map(function (c) { return { label: c.component, count: c.products.length }; })),
        after: function () {
          body.querySelector('.stp-em-mlist').onclick = function (e) {
            var b = e.target.closest('button'); if (!b) return;
            showProducts(comps[parseInt(b.getAttribute('data-i'), 10)]);
          };
        }
      });
    }
    function showProducts(comp) {
      show({
        title: comp.component,
        html: '<div class="stp-em-cards stp-em-cards--mob">' + comp.products.map(cardSkeleton).join('') + '</div>',
        after: function () { hydrateCards(body); }
      });
    }
    sheet.querySelector('.stp-em-back').addEventListener('click', back);
    sheet.querySelector('.stp-em-close').addEventListener('click', close);
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && !sheet.hidden) close(); });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', buildMobile);
  else buildMobile();
})();
